import type { BoothVaultSeedKey } from './booth.schema';

export const BOOTH_VAULT_ADMIN_ALG_NONE_JWT =
  'eyJhbGciOiJub25lIiwidHlwIjoiSldUIn0.eyJzdWIiOiIxMDAyIiwicm9sZSI6ImFkbWluIn0.';

export type BoothVaultHarSeed = {
  seedKey: BoothVaultSeedKey;
  method: 'GET';
  path: string;
  requestHeaders?: ReadonlyArray<{ name: string; value: string }>;
  responseStatus: number;
  responseJson: Record<string, unknown>;
};

const maintenanceBody: Record<string, unknown> = {
  service: 'gold-vault-ops',
  status: 'degraded',
  lastIncident: '2026-07-14T03:12:00Z',
  summary:
    'Nightly vault lock re-synced with manager notebook service. Guest-facing APIs must not expose staff roster.',
  internalNote:
    'If the gate rejects codes, check branch manager profile in customer API (staff records are tagged tier=staff). Manager on duty: customer id 1002.',
  leakedBy: 'automated maintenance bot',
  storyBeat: 'recon',
  hint: 'The maintenance log names a branch manager stored in customer records. Pull staff profile GET /api/customer/1002 (not your guest id 1001).',
};

const customer1002Body: Record<string, unknown> = {
  customerId: '1002',
  name: 'Dana Vault — Branch Manager',
  tier: 'staff',
  role: 'branch_manager',
  internalSystems: ['manager-notebook', 'vault-audit'],
  notebookEndpoint: '/api/admin/vault-notes',
  authNote:
    'Managers authenticate with JWT from /api/token. Notebook requires role=admin.',
  reminder:
    'Do not store the live gate combo in customer records — it belongs only in encrypted manager notes.',
  storyBeat: 'idor',
  hint: 'Dana’s record points at the manager notebook API. Request GET /api/token, forge an unsigned admin token (alg:none), and open GET /api/admin/vault-notes.',
};

const vaultNotesBody: Record<string, unknown> = {
  title: 'Manager vault notebook — CONFIDENTIAL',
  classification: 'restricted',
  author: 'Dana Vault',
  entries: [
    {
      at: '2026-07-14T22:00:00Z',
      subject: 'Tonight’s gate combo (rotate after event)',
      body: 'Audit confirmed. The gate combo is bound to the active vault session — authenticate and read this endpoint live.',
    },
    {
      at: '2026-07-14T21:40:00Z',
      subject: 'Reminder',
      body: 'Never paste the combo into customer CRM — maintenance already leaked the notebook sync once.',
    },
  ],
  storyBeat: 'jwt',
  hint: 'Forge an alg:none admin JWT and replay GET /api/admin/vault-notes with your heist session cookie to read tonight’s combo.',
};

export const BOOTH_VAULT_HAR_SEEDS: readonly BoothVaultHarSeed[] = [
  {
    seedKey: 'maintenance',
    method: 'GET',
    path: '/api/maintenance',
    responseStatus: 200,
    responseJson: maintenanceBody,
  },
  {
    seedKey: 'customer-idor',
    method: 'GET',
    path: '/api/customer/1002',
    responseStatus: 200,
    responseJson: customer1002Body,
  },
  {
    seedKey: 'vault-notes',
    method: 'GET',
    path: '/api/admin/vault-notes',
    requestHeaders: [
      {
        name: 'Authorization',
        value: `Bearer ${BOOTH_VAULT_ADMIN_ALG_NONE_JWT}`,
      },
    ],
    responseStatus: 200,
    responseJson: vaultNotesBody,
  },
] as const;

export const BOOTH_VAULT_HAR_PATHS: readonly string[] =
  BOOTH_VAULT_HAR_SEEDS.map((seed) => seed.path);
